import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, TextField, Alert, IconButton, Grid, MenuItem, Typography, Tooltip,
  FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { superAdminAPI } from '../../services/api';
import {
  SuperAdminDataTable, apiErrorMessage, statusChip, AdminTableEditButton, STATUS_FILTER_OPTIONS,
} from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import {
  RELATIONSHIP_MODULE_OPTIONS,
  RELATION_TYPES,
  buildRelationshipLabel,
  getModuleLabel,
  getRelationTypeLabel,
} from '../../constants/relationshipRules';
import { RADIUS } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const defaultForm = {
  sourceModule: 'trips',
  targetModule: 'vehicles',
  relationType: 'lookup',
  label: '',
  allowed: true,
  status: 'active',
};

const ALLOWED_FILTER_OPTIONS = [
  { label: 'Allowed', value: 'true' },
  { label: 'Not Allowed', value: 'false' },
];

const RELATION_TYPE_FILTER_OPTIONS = RELATION_TYPES.map((t) => ({
  label: t.label,
  value: t.value,
}));

export const RelationshipRulesPage: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(defaultForm);
  const [detailRow, setDetailRow] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [labelEdited, setLabelEdited] = useState(false);

  const moduleFilterOptions = useMemo(
    () => RELATIONSHIP_MODULE_OPTIONS.map((m) => ({ label: m.label, value: m.value })),
    []
  );

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await superAdminAPI.getRelationshipRules();
      setRules(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load rules'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditId(null);
    setLabelEdited(false);
    setForm({
      ...defaultForm,
      label: buildRelationshipLabel(defaultForm.sourceModule, defaultForm.targetModule, defaultForm.relationType),
    });
    setOpen(true);
  };

  const openEdit = (rule: any) => {
    setEditId(rule._id);
    setLabelEdited(Boolean(rule.label));
    setForm({
      sourceModule: rule.sourceModule || 'trips',
      targetModule: rule.targetModule || 'vehicles',
      relationType: rule.relationType || 'lookup',
      label: rule.label || '',
      allowed: rule.allowed ?? true,
      status: rule.status || 'active',
    });
    setOpen(true);
  };

  const updateFormField = (patch: Partial<typeof form>) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (!labelEdited && (patch.sourceModule || patch.targetModule || patch.relationType)) {
        next.label = buildRelationshipLabel(
          next.sourceModule,
          next.targetModule,
          next.relationType
        );
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.sourceModule || !form.targetModule) {
      setError('Source and target modules are required');
      return;
    }
    if (form.sourceModule === form.targetModule) {
      setError('Source and target modules must be different');
      return;
    }
    try {
      const payload = {
        ...form,
        label: form.label || buildRelationshipLabel(form.sourceModule, form.targetModule, form.relationType),
      };
      if (editId) {
        await superAdminAPI.updateRelationshipRule(editId, payload);
        setMessage('Rule updated');
      } else {
        await superAdminAPI.createRelationshipRule(payload);
        setMessage('Rule created');
      }
      setOpen(false);
      setEditId(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, editId ? 'Update failed' : 'Create failed'));
    }
  };

  const buildRuleFields = (r: any): SuperAdminDetailField[] => [
    { label: 'Label', value: r.label || buildRelationshipLabel(r.sourceModule, r.targetModule, r.relationType), fullWidth: true },
    { label: 'Source Module', value: getModuleLabel(r.sourceModule) },
    { label: 'Target Module', value: getModuleLabel(r.targetModule) },
    { label: 'Relationship Type', value: getRelationTypeLabel(r.relationType) },
    { label: 'Allowed', value: r.allowed ? 'Yes' : 'No' },
    { label: 'Rule ID', value: String(r._id || ''), fullWidth: true },
  ];

  return (
    <Box>
      <PageHeader
        title="Relationship Rules"
        subtitle={getPageSubtitle('relationship_rules')}
        icon={<AccountTreeIcon />}

        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={catchyPrimaryButtonSx}>
            Add Rule
          </Button>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && !open && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <SuperAdminDataTable
        rows={rules}
        loading={loading}
        error={error && !open ? error : undefined}
        exportFileName="super-admin-relationship-rules"
        exportSheetName="Relationship Rules"
        statusField="status"
        searchPlaceholder="Search modules or relationship type..."
        columns={[
          {
            id: 'label',
            header: 'Label',
            searchValue: (r) => `${r.label} ${r.sourceModule} ${r.targetModule} ${r.relationType}`,
            exportValue: (r) => r.label || buildRelationshipLabel(r.sourceModule, r.targetModule, r.relationType),
            render: (r) => (
              <strong>
                {r.label || buildRelationshipLabel(r.sourceModule, r.targetModule, r.relationType)}
              </strong>
            ),
          },
          {
            id: 'source',
            header: 'Source Module',
            searchValue: (r) => `${r.sourceModule} ${getModuleLabel(r.sourceModule)}`,
            exportValue: (r) => getModuleLabel(r.sourceModule),
            filterType: 'select',
            filterOptions: moduleFilterOptions,
            filterValue: (r) => r.sourceModule,
            render: (r) => getModuleLabel(r.sourceModule),
          },
          {
            id: 'target',
            header: 'Target Module',
            searchValue: (r) => `${r.targetModule} ${getModuleLabel(r.targetModule)}`,
            exportValue: (r) => getModuleLabel(r.targetModule),
            filterType: 'select',
            filterOptions: moduleFilterOptions,
            filterValue: (r) => r.targetModule,
            render: (r) => getModuleLabel(r.targetModule),
          },
          {
            id: 'relationType',
            header: 'Relationship Type',
            searchValue: (r) => `${r.relationType} ${getRelationTypeLabel(r.relationType)}`,
            exportValue: (r) => getRelationTypeLabel(r.relationType),
            filterType: 'select',
            filterOptions: RELATION_TYPE_FILTER_OPTIONS,
            filterValue: (r) => r.relationType || 'lookup',
            render: (r) => getRelationTypeLabel(r.relationType),
          },
          {
            id: 'allowed',
            header: 'Allowed',
            exportValue: (r) => r.allowed ? 'Allowed' : 'Not Allowed',
            filterType: 'select',
            filterOptions: ALLOWED_FILTER_OPTIONS,
            filterValue: (r) => String(r.allowed),
            render: (r) => statusChip(r.allowed ? 'active' : 'inactive'),
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (r) => r.status,
            filterType: 'select',
            filterOptions: STATUS_FILTER_OPTIONS,
            filterValue: (r) => r.status,
            render: (r) => statusChip(r.status),
          },
        ]}
        onRowClick={setDetailRow}
        renderActions={(r) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <AdminTableEditButton onClick={() => openEdit(r)} />
            <Tooltip title="Delete">
              <IconButton size="small" onClick={async (e) => {
                e.stopPropagation();
                await superAdminAPI.deleteRelationshipRule(r._id);
                load();
              }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle="Relationship Rule"
        title={detailRow ? (detailRow.label || buildRelationshipLabel(detailRow.sourceModule, detailRow.targetModule, detailRow.relationType)) : ''}
        subtitle={detailRow ? `${getModuleLabel(detailRow.sourceModule)} → ${getModuleLabel(detailRow.targetModule)}` : undefined}
        status={detailRow?.status}
        fields={detailRow ? buildRuleFields(detailRow) : []}
        primaryActions={detailRow ? (
          <Button variant="contained" onClick={() => { openEdit(detailRow); setDetailRow(null); }} sx={catchyPrimaryButtonSx}>
            Edit Rule
          </Button>
        ) : undefined}
      />

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: `${RADIUS}px`, p: 3, minWidth: 480 } }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          {editId ? 'Edit Relationship Rule' : 'Add Relationship Rule'}
        </Typography>
        {error && open && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Source Module"
              fullWidth
              size="small"
              sx={catchyTextFieldSx}
              value={form.sourceModule}
              onChange={(e) => updateFormField({ sourceModule: e.target.value })}
            >
              {RELATIONSHIP_MODULE_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Target Module"
              fullWidth
              size="small"
              sx={catchyTextFieldSx}
              value={form.targetModule}
              onChange={(e) => updateFormField({ targetModule: e.target.value })}
            >
              {RELATIONSHIP_MODULE_OPTIONS
                .filter((m) => m.value !== form.sourceModule)
                .map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Relationship Type"
              fullWidth
              size="small"
              sx={catchyTextFieldSx}
              value={form.relationType}
              onChange={(e) => updateFormField({ relationType: e.target.value })}
              helperText="How the source module links to the target module"
            >
              {RELATION_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Display Label"
              fullWidth
              size="small"
              sx={catchyTextFieldSx}
              value={form.label}
              onChange={(e) => {
                setLabelEdited(true);
                setForm((f) => ({ ...f, label: e.target.value }));
              }}
              helperText="Auto-generated from modules and type; edit to customize"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.allowed}
                  onChange={(e) => setForm({ ...form, allowed: e.target.checked })}
                  color="success"
                />
              }
              label="Relationship allowed for tenants"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Status"
              fullWidth
              size="small"
              sx={catchyTextFieldSx}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSave} sx={catchyPrimaryButtonSx}>
              {editId ? 'Save Changes' : 'Create Rule'}
            </Button>
          </Grid>
        </Grid>
      </Dialog>
    </Box>
  );
};
